import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';



export function FaqSection({ faqs, loading, error }) {
  const [openIndex, setOpenIndex] = useState(null);
  const { translate } = useLanguage();

  // Helper: safely get string value from CMS field object or plain string
  const getValue = (field, fallback = '') => {
    if (!field) return fallback;
    if (typeof field === 'string') return field;
    if (field && typeof field === 'object' && 'value' in field) {
      return field.value ?? fallback;
    }
    return fallback;
  };

  // Clean HTML from answer → plain text with basic formatting
  const cleanAnswer = (html = '') => {
    if (!html) return 'No answer provided.';

    let text = html
      .replace(/<\/?(p|div|br)[^>]*>/gi, '\n')
      .replace(/<li[^>]*>/gi, '• ')
      .replace(/<\/li>/gi, '\n')
      .replace(/<ol[^>]*>/gi, '')
      .replace(/<\/ol>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/\n\s*\n/g, '\n')
      .trim();

    return text || 'No answer provided.';
  };

  if (loading) {
    return (
      <section className="bg-bg-medium py-8 sm:py-12 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-20">
            <div className="animate-pulse h-8 w-64 bg-gray-700 rounded mx-auto mb-6"></div>
            <div className="space-y-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-800 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }
if ( faqs.length === 0){
  return null;
}
  if (error) {
    return (
      <section className="bg-bg-medium py-8 sm:py-12 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-16">
          <h2 className="text-3xl font-bold text-white mb-4">
            {translate('newBuyer.faq.title') || 'Frequently Asked Questions'}
          </h2>
          <p className="text-gray-400">
            {translate('common.noData') || 'No FAQs available at the moment.'}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-bg-medium py-8 sm:py-12 lg:py-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            {translate('newBuyer.faq.title') || 'Frequently Asked Questions'}
          </h2>
          <p className="text-gray-400 text-base">
            {translate('newBuyer.faq.subtitle') || 'Find answers to common questions about buying at Al Qaryah Auctions.'}
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, idx) => {
            const question = getValue(faq.question);
            const cleanAns = cleanAnswer(getValue(faq.answer));

            return (
              <div
                key={idx}
                className="bg-bg-medium border border-gray-800 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                  className="w-full px-6 py-5 sm:px-8 sm:py-6 flex items-center justify-between hover:bg-bg-medium transition-colors text-left"
                >
                  <span className="text-base sm:text-lg font-semibold text-white pr-4">
                    {question || 'Question not available'}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${
                      openIndex === idx ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>

                {openIndex === idx && (
                  <div className="px-6 py-4 sm:px-8 sm:py-5 bg-bg-medium border-t border-gray-800">
                    <p className="text-gray-400 text-base leading-relaxed whitespace-pre-wrap">
                      {cleanAns}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}